import { Button } from "../../Button";
import { Icon, type IconName } from "../../Icon";
import { Skeleton } from "../../Skeleton";
import type { CellComponentProps } from "../DataTable.types";
import { getCellContainerClasses, resolveValue, SKELETON_SIZES } from "./cellUtils";

/**
 * Action configuration for ActionCell
 */
export interface Action<T = any> {
  id: string;
  label?: string;
  icon?: IconName;
  onClick: (row: T) => void;
  disabled?: boolean | ((row: T) => boolean);
  hidden?: boolean | ((row: T) => boolean);
  variant?: "primary" | "secondary" | "icon";
}

export interface ActionCellProps<T = any> extends CellComponentProps<T> {
  actions: Action<T>[] | ((row: T) => Action<T>[]);
  align?: "left" | "center" | "right";
  size?: "sm" | "md" | "lg";
}

/**
 * ActionCell
 *
 * DataTable cell component for rendering action buttons within table rows.
 * Supports multiple actions with icons, labels, and conditional visibility/disabled states.
 */
export function ActionCell<T = any>({ row, context, actions, align = "right", size = "sm" }: ActionCellProps<T>) {
  const { isLoading } = context;

  if (isLoading) {
    return <Skeleton className={SKELETON_SIZES.sm} />;
  }

  const actionArray = typeof actions === "function" ? actions(row) : actions;

  const visibleActions = actionArray.filter((action) => {
    const hidden = resolveValue(action.hidden, row);
    return !hidden;
  });

  if (visibleActions.length === 0) {
    return null;
  }

  const className = getCellContainerClasses(align);

  return (
    <div className={className}>
      {visibleActions.map((action) => {
        const disabled = resolveValue(action.disabled, row);

        if (action.variant === "icon" || !action.label) {
          return (
            <Button
              key={action.id}
              variant="icon"
              size={size}
              onPress={() => {
                action.onClick(row);
              }}
              isDisabled={disabled}
              aria-label={action.label || action.id}
            >
              <Icon name={action.icon || "DotsThree"} size={size === "sm" ? 16 : 20} />
            </Button>
          );
        }

        return (
          <Button
            key={action.id}
            variant={action.variant || "secondary"}
            size={size}
            onPress={() => {
              action.onClick(row);
            }}
            isDisabled={disabled}
          >
            {action.icon && <Icon name={action.icon} size={size === "sm" ? 14 : 16} />}
            {action.label}
          </Button>
        );
      })}
    </div>
  );
}
